import React, { useState, useEffect } from "react";
import { useParams, Link, useHistory } from "react-router-dom";
import { Helmet } from "react-helmet";
import styled from "styled-components";
import { ClipboardList, Info, X, ChevronLeft, ChevronRight, Eye, SlidersHorizontal } from "lucide-react";
import { useUserBetsQuery } from "hooks/useWallet";
import Cookies from "js-cookie";
import { getUID } from "utils/Helper";

const PageContainer = styled.div`
  min-height: 80vh;
  color: #fff;
  padding: 24px 16px;
  font-family: Arial, sans-serif;
  max-width: 1100px;
  margin: 0 auto;
  @media (max-width: 768px) {
    padding: 16px 12px;
  }
`;

const ContentCard = styled.div`
  width: 100%;
`;

const TopHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 24px;
  font-weight: 800;
  color: #fff;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #b9c4c7;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  &:hover {
    color: #fff;
    background-color: #292d2e;
  }
`;

const MainGrid = styled.div`
  display: grid;
  grid-template-columns: 220px 1fr;
  gap: 24px;
  min-width: 0;
  @media (max-width: 768px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 16px;
  }
`;

const Sidebar = styled.aside`
  background-color: #292d2e;
  padding: 8px;
  border-radius: 8px;
  height: fit-content;
  @media (max-width: 768px) {
    position: static;
    margin-bottom: 4px;
  }
`;

const SidebarNav = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  @media (max-width: 768px) {
    flex-direction: row;
    overflow-x: auto;
    white-space: nowrap;
    gap: 8px;
    padding-bottom: 4px;
    padding-left: 2px;
    padding-right: 2px;
    
    &::-webkit-scrollbar {
      display: none;
    }
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
`;

const SidebarLink = styled(Link)`
  display: block;
  padding: 12px 16px;
  color: ${props => props.active ? "#fff" : "#b9c4c7"};
  background-color: ${props => props.active ? "#3a4142" : "transparent"};
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s ease;
  white-space: nowrap;
  &:hover {
    color: #fff;
    background-color: ${props => props.active ? "#3a4142" : "rgba(58, 65, 66, 0.4)"};
    text-decoration: none;
  }
  @media (max-width: 768px) {
    display: inline-block;
    padding: 8px 12px;
    font-size: 13px;
  }
`;

const Panel = styled.section`
  min-width: 0;
  width: 100%;
`;

const SubTabs = styled.div`
  display: flex;
  gap: 8px;
  border-bottom: 1px solid rgba(58, 65, 66, 0.4);
  padding-bottom: 12px;
  margin-bottom: 8px;
  overflow-x: auto;
  white-space: nowrap;
  
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const SubTabButton = styled(Link)`
  padding: 8px 16px;
  border-radius: 9999px;
  color: ${props => props.active ? "#fff" : "#b9c4c7"};
  background-color: ${props => props.active ? "#3a4142" : "transparent"};
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
  transition: all 0.2s ease;
  &:hover {
    color: #fff;
    background-color: ${props => props.active ? "#3a4142" : "rgba(58, 65, 66, 0.4)"};
    text-decoration: none;
  }
`;

const TableCard = styled.div`
  background-color: rgba(41, 45, 46, 0.4);
  border-radius: 8px;
  padding: 16px;
  overflow-x: auto;
  max-width: 100%;
  border: 1px solid rgba(58, 65, 66, 0.4);
  @media (max-width: 768px) {
    padding: 8px;
  }
`;

const TableContent = styled.div`
  min-width: 640px;
`;

const Row6 = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 1fr 1fr 1fr 1.2fr 1fr;
  padding: 12px 8px;
  font-size: 14px;
  font-weight: ${props => props.header ? "bold" : "normal"};
  color: ${props => props.header ? "#b9c4c7" : "#fff"};
  border-bottom: 1px solid rgba(58, 65, 66, 0.4);
  align-items: center;
  transition: background-color 0.2s ease;
  
  ${props => !props.header && `
    &:hover {
      background-color: rgba(58, 65, 66, 0.2);
    }
  `}
  @media (max-width: 768px) {
    font-size: 12.5px;
    padding: 10px 4px;
  }
`;

const ColLeft = styled.span`
  text-align: left;
`;

const ColCenter = styled.span`
  text-align: center;
  ${props => props.bold && "font-weight: bold;"}
  ${props => props.color && `color: ${props.color};`}
`;

const ColRight = styled.span`
  text-align: right;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: bold;
  text-transform: uppercase;
  background-color: ${props => {
    switch (props.status?.toLowerCase()) {
      case "won":
      case "success":
        return "rgba(31, 193, 107, 0.15)";
      case "pending":
      case "active":
        return "rgba(255, 193, 7, 0.15)";
      default:
        return "rgba(244, 67, 54, 0.15)";
    }
  }};
  color: ${props => {
    switch (props.status?.toLowerCase()) {
      case "won":
      case "success":
        return "#1fc16b";
      case "pending":
      case "active":
        return "#ffc107";
      default:
        return "#ef4444";
    }
  }};
`;

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 0;
  color: #b9c4c7;
`;

const Loader = styled.div`
  border: 4px solid rgba(255,255,255,0.1);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border-left-color: #1fc16b;
  animation: spin 1s linear infinite;
  margin: 40px auto;
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ToggleFiltersButton = styled.button`
  display: none;
  background-color: #292d2e;
  color: #fff;
  border: 1px solid rgba(58, 65, 66, 0.6);
  border-radius: 8px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 12px;
  width: 100%;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #3a4142;
  }
  
  @media (max-width: 768px) {
    display: flex;
  }
`;

const FilterBar = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
  background-color: #292d2e;
  padding: 16px;
  border-radius: 8px;
  align-items: end;
  border: 1px solid rgba(58, 65, 66, 0.4);

  @media (max-width: 768px) {
    display: ${props => props.show ? "grid" : "none"};
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
    padding: 10px;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 11px;
  color: #b9c4c7;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const SelectInput = styled.select`
  background-color: #1a1d1e;
  color: #fff;
  border: 1px solid rgba(58, 65, 66, 0.6);
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 13px;
  outline: none;
  cursor: pointer;
  transition: border-color 0.2s;
  &:focus {
    border-color: #1fc16b;
  }
  @media (max-width: 768px) {
    padding: 6px 8px;
    font-size: 12px;
  }
`;

const TextInput = styled.input`
  background-color: #1a1d1e;
  color: #fff;
  border: 1px solid rgba(58, 65, 66, 0.6);
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 13px;
  outline: none;
  transition: border-color 0.2s;
  &:focus {
    border-color: #1fc16b;
  }
  @media (max-width: 768px) {
    padding: 6px 8px;
    font-size: 12px;
  }
`;

const DateInput = styled.input`
  background-color: #1a1d1e;
  color: #fff;
  border: 1px solid rgba(58, 65, 66, 0.6);
  border-radius: 6px;
  padding: 7px 10px;
  font-size: 13px;
  outline: none;
  color-scheme: dark;
  transition: border-color 0.2s;
  &:focus {
    border-color: #1fc16b;
  }
  @media (max-width: 768px) {
    padding: 5px 8px;
    font-size: 12px;
  }
`;

const PaginationBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
  flex-wrap: wrap;
  gap: 12px;
  padding: 4px 8px;

  @media (max-width: 768px) {
    justify-content: center;
    flex-direction: column;
    text-align: center;
    gap: 8px;
  }
`;

const PaginationButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PageButton = styled.button`
  background-color: ${props => props.active ? "#1fc16b" : "#3a4142"};
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  cursor: ${props => props.disabled ? "not-allowed" : "pointer"};
  opacity: ${props => props.disabled ? 0.5 : 1};
  transition: all 0.2s;
  &:hover:not(:disabled) {
    background-color: ${props => props.active ? "#1fc16b" : "#1fc16b"};
  }
`;

const PageInfoText = styled.span`
  font-size: 13px;
  color: #b9c4c7;
`;

const ViewButton = styled.button`
  background: none;
  border: none;
  color: #1fc16b;
  cursor: pointer;
  padding: 6px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  &:hover {
    background-color: rgba(31, 193, 107, 0.15);
    transform: scale(1.05);
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.85);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1100;
  padding: 16px;
  backdrop-filter: blur(4px);
`;

const ModalContent = styled.div`
  background-color: #232626;
  border-radius: 12px;
  width: 100%;
  max-width: 480px;
  border: 1px solid rgba(58, 65, 66, 0.6);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
  overflow: hidden;
  animation: modalEnter 0.25s ease-out;

  @keyframes modalEnter {
    from {
      transform: scale(0.95);
      opacity: 0;
    }
    to {
      transform: scale(1);
      opacity: 1;
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background-color: #292d2e;
  border-bottom: 1px solid rgba(58, 65, 66, 0.4);
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ModalClose = styled.button`
  background: none;
  border: none;
  color: #b9c4c7;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;
  &:hover {
    color: #fff;
    background-color: rgba(58, 65, 66, 0.6);
  }
`;

const ModalBody = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  max-height: 60vh;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: rgba(58, 65, 66, 0.2);
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(58, 65, 66, 0.8);
    border-radius: 3px;
  }
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(58, 65, 66, 0.2);
  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

const DetailLabel = styled.span`
  color: #b9c4c7;
  font-weight: 600;
  font-size: 13px;
`;

const DetailValue = styled.span`
  color: #fff;
  font-weight: 500;
  text-align: right;
  word-break: break-all;
  max-width: 250px;
  font-size: 13.5px;
`;

const SECTIONS = [
  { slug: "api-games", label: "API Games" },
  { slug: "archive", label: "Archive" }
];

const SUB_TABS = [
  { slug: "settled", label: "Settled" },
  { slug: "active", label: "Active" }
];

const getPresetDates = (preset) => {
  const today = new Date();
  const format = (d) => d.toISOString().split("T")[0];

  switch (preset) {
    case "today":
      return { start: format(today), end: format(today) };
    case "yesterday": {
      const yesterday = new Date();
      yesterday.setDate(today.getDate() - 1);
      return { start: format(yesterday), end: format(yesterday) };
    }
    case "last7": {
      const last7 = new Date();
      last7.setDate(today.getDate() - 7);
      return { start: format(last7), end: format(today) };
    }
    case "all":
    default:
      return { start: "", end: "" };
  }
};

function MyBets({ t }) {
  const { section = "api-games", sub } = useParams();
  const history = useHistory();
  const uid = getUID || parseFloat(Cookies.get("uid"));

  // Sub tab is defined only if section is api-games
  const hasSubTabs = section === "api-games";
  const activeSub = hasSubTabs ? (sub || "settled") : "";

  // Filters State
  const [datePreset, setDatePreset] = useState("all");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [currencyFilter, setCurrencyFilter] = useState("all");
  const [searchFilter, setSearchFilter] = useState("");
  const [limitFilter, setLimitFilter] = useState(10);
  const [page, setPage] = useState(1);
  const [selectedBet, setSelectedBet] = useState(null);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // Compute start/end dates
  const dates = getPresetDates(datePreset);
  const startDate = datePreset === "custom" ? customStart : dates.start;
  const endDate = datePreset === "custom" ? customEnd : dates.end;

  const filters = {
    page,
    limit: limitFilter,
    startDate,
    endDate,
    currency: currencyFilter,
    search: searchFilter
  };

  const { data = {}, isLoading, error } = useUserBetsQuery(section, activeSub, uid, filters);

  const betsData = data.data || [];
  const pagination = data.pagination || { currentPage: 1, pageSize: 10, totalRecords: 0, totalPages: 1 };

  const close = () => {
    history.push("/");
  };

  // Reset pagination when tab/filter changes
  useEffect(() => {
    setPage(1);
  }, [section, sub, datePreset, customStart, customEnd, currencyFilter, searchFilter, limitFilter]);

  return (
    <PageContainer>
      <Helmet>
        <title>My Bets - Winsmash</title>
      </Helmet>
      
      <ContentCard>
        <TopHeader>
          <HeaderTitle>
            <ClipboardList size={28} style={{ color: "#1fc16b" }} />
            My Bets
          </HeaderTitle>
          <CloseButton onClick={close}>
            <X size={24} />
          </CloseButton>
        </TopHeader>

        <MainGrid>
          <Sidebar>
            <SidebarNav>
              {SECTIONS.map(sec => (
                <SidebarLink
                  key={sec.slug}
                  to={`/my-bets/${sec.slug}${sec.slug !== "archive" ? "/settled" : ""}`}
                  active={section === sec.slug ? 1 : 0}
                >
                  {sec.label}
                </SidebarLink>
              ))}
            </SidebarNav>
          </Sidebar>

          <Panel>
            {hasSubTabs && (
              <SubTabs>
                {SUB_TABS.map(s => (
                  <SubTabButton
                    key={s.slug}
                    to={`/my-bets/${section}/${s.slug}`}
                    active={activeSub === s.slug ? 1 : 0}
                  >
                    {s.label}
                  </SubTabButton>
                ))}
              </SubTabs>
            )}

            {/* Filter Bar */}
            <ToggleFiltersButton onClick={() => setShowMobileFilters(s => !s)}>
              <SlidersHorizontal size={18} style={{ color: "#1fc16b" }} />
              {showMobileFilters ? "Hide Filters" : "Show Filters"}
            </ToggleFiltersButton>

            <FilterBar show={showMobileFilters}>
              <FilterGroup>
                <Label>Search Bet ID / Game</Label>
                <TextInput 
                  type="text" 
                  placeholder="Search Bet ID or Game..." 
                  value={searchFilter} 
                  onChange={(e) => setSearchFilter(e.target.value)}
                />
              </FilterGroup>

              <FilterGroup>
                <Label>Date Preset</Label>
                <SelectInput value={datePreset} onChange={(e) => setDatePreset(e.target.value)}>
                  <option value="all">All Time</option>
                  <option value="today">Today</option>
                  <option value="yesterday">Yesterday</option>
                  <option value="last7">Last 7 Days</option>
                  <option value="custom">Custom Range</option>
                </SelectInput>
              </FilterGroup>

              {datePreset === "custom" && (
                <>
                  <FilterGroup>
                    <Label>Start Date</Label>
                    <DateInput 
                      type="date" 
                      value={customStart} 
                      onChange={(e) => setCustomStart(e.target.value)}
                    />
                  </FilterGroup>
                  <FilterGroup>
                    <Label>End Date</Label>
                    <DateInput 
                      type="date" 
                      value={customEnd} 
                      onChange={(e) => setCustomEnd(e.target.value)}
                    />
                  </FilterGroup>
                </>
              )}

              <FilterGroup>
                <Label>Currency</Label>
                <SelectInput value={currencyFilter} onChange={(e) => setCurrencyFilter(e.target.value)}>
                  <option value="all">All Currencies</option>
                  <option value="inr">INR</option>
                  <option value="usdt">USDT</option>
                  <option value="trx">TRX</option>
                </SelectInput>
              </FilterGroup>

              <FilterGroup>
                <Label>Rows Per Page</Label>
                <SelectInput value={limitFilter} onChange={(e) => setLimitFilter(Number(e.target.value))}>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </SelectInput>
              </FilterGroup>
            </FilterBar>

            <TableCard>
              <TableContent>
                <Row6 header>
                  <ColLeft>Date & Time</ColLeft>
                  <ColCenter>Game</ColCenter>
                  <ColCenter>Bet Amount</ColCenter>
                  <ColCenter>Payout</ColCenter>
                  <ColCenter>Status</ColCenter>
                  <ColRight>Action</ColRight>
                </Row6>

                {isLoading && <Loader />}

                {!isLoading && (error || betsData.length === 0) && (
                  <EmptyStateContainer>
                    <svg width="56" height="56" viewBox="0 0 56 56" fill="none" style={{ opacity: 0.4 }}>
                      <rect x="8" y="32" width="10" height="16" rx="2" fill="currentColor" />
                      <rect x="23" y="22" width="10" height="26" rx="2" fill="currentColor" />
                      <rect x="38" y="14" width="10" height="34" rx="2" fill="currentColor" />
                      <circle cx="43" cy="14" r="3" fill="#1fc16b" />
                    </svg>
                    <div style={{ marginTop: "16px", fontSize: "14px" }}>No Bets History Found</div>
                  </EmptyStateContainer>
                )}

                {!isLoading && betsData.length > 0 && (
                  <div>
                    {betsData.map((bet, idx) => (
                      <Row6 key={idx}>
                        <ColLeft>
                          {new Date(bet.created_at).toLocaleString()}
                        </ColLeft>
                        <ColCenter style={{ textTransform: "capitalize", fontSize: "13px" }}>
                          {bet.game}
                        </ColCenter>
                        <ColCenter bold color="#fff">
                          {parseFloat(bet.amount).toFixed(2)} {bet.currency}
                        </ColCenter>
                        <ColCenter bold color={parseFloat(bet.payout) > 0 ? "#1fc16b" : "#b9c4c7"}>
                          {parseFloat(bet.payout) > 0 ? `+${parseFloat(bet.payout).toFixed(2)} (${bet.multiplier})` : `0.00 (${bet.multiplier})`}
                        </ColCenter>
                        <ColCenter>
                          <StatusBadge status={bet.status}>{bet.status}</StatusBadge>
                        </ColCenter>
                        <ColRight>
                          <ViewButton onClick={() => setSelectedBet(bet)} title="View Details">
                            <Eye size={18} />
                          </ViewButton>
                        </ColRight>
                      </Row6>
                    ))}
                  </div>
                )}
              </TableContent>
            </TableCard>

            {/* Pagination Bar */}
            {!isLoading && betsData.length > 0 && (
              <PaginationBar>
                <PageInfoText>
                  Showing {((page - 1) * limitFilter) + 1} to {Math.min(page * limitFilter, pagination.totalRecords)} of {pagination.totalRecords} entries
                </PageInfoText>
                <PaginationButtons>
                  <PageButton 
                    disabled={page === 1} 
                    onClick={() => setPage(p => Math.max(p - 1, 1))}
                  >
                    <ChevronLeft size={16} /> Prev
                  </PageButton>
                  <PageButton active>
                    {page}
                  </PageButton>
                  <PageButton 
                    disabled={page >= pagination.totalPages} 
                    onClick={() => setPage(p => Math.min(p + 1, pagination.totalPages))}
                  >
                    Next <ChevronRight size={16} />
                  </PageButton>
                </PaginationButtons>
              </PaginationBar>
            )}
          </Panel>
        </MainGrid>
      </ContentCard>

      {/* Bet Details Modal */}
      {selectedBet && (
        <ModalOverlay onClick={() => setSelectedBet(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Bet Details</ModalTitle>
              <ModalClose onClick={() => setSelectedBet(null)}>
                <X size={20} />
              </ModalClose>
            </ModalHeader>
            <ModalBody>
              <DetailRow>
                <DetailLabel>Bet ID / Ref</DetailLabel>
                <DetailValue>{selectedBet.id || "—"}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>Game</DetailLabel>
                <DetailValue style={{ textTransform: "capitalize" }}>{selectedBet.game}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>Date & Time</DetailLabel>
                <DetailValue>{new Date(selectedBet.created_at).toLocaleString()}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>Bet Amount</DetailLabel>
                <DetailValue style={{ color: "#fff", fontWeight: "bold" }}>
                  {parseFloat(selectedBet.amount).toFixed(2)} {selectedBet.currency}
                </DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>Multiplier</DetailLabel>
                <DetailValue>{selectedBet.multiplier}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>Payout</DetailLabel>
                <DetailValue style={{ color: parseFloat(selectedBet.payout) > 0 ? "#1fc16b" : "#fff", fontWeight: "bold" }}>
                  {parseFloat(selectedBet.payout).toFixed(2)} {selectedBet.currency}
                </DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>Status</DetailLabel>
                <DetailValue>
                  <StatusBadge status={selectedBet.status}>{selectedBet.status}</StatusBadge>
                </DetailValue>
              </DetailRow>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}
    </PageContainer>
  );
}

export default MyBets;
